import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { CheckCircle, TrendUp, TrendDown, Clock, Warning, Info } from "@phosphor-icons/react";

const REASON_TONE = {
  primary: "bg-primary/10 text-primary",
  danger: "bg-red-100 text-red-700",
  warning: "bg-amber-100 text-amber-800",
  success: "bg-emerald-100 text-emerald-700",
  info: "bg-sky-100 text-sky-700",
  muted: "bg-muted text-muted-foreground",
};

const PRIORITY_BADGE = {
  critical: "bg-red-600 text-white hover:bg-red-600",
  high: "bg-red-100 text-red-700 hover:bg-red-100",
  normal: "bg-muted text-muted-foreground",
};

function TrendChip({ pct }) {
  if (Math.abs(pct) < 5) {
    return <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">stable</span>;
  }
  const up = pct > 0;
  const Icon = up ? TrendUp : TrendDown;
  const cls = up ? "text-emerald-600" : "text-red-600";
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] mono ${cls}`}>
      <Icon size={12} weight="bold"/>{Math.abs(pct)}%
    </span>
  );
}

export default function Planning() {
  const [recs, setRecs] = useState([]);
  const [totalCap, setTotalCap] = useState(0);
  const [settings, setSettings] = useState(null);
  const [selected, setSelected] = useState({});
  const [overrides, setOverrides] = useState({});
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const { data } = await api.get("/planner/recommendations");
    setRecs(data.recommendations);
    setTotalCap(data.total_machine_capacity_kg);
    setSettings(data.settings);
  };
  useEffect(() => { load(); }, []);

  const toggle = (id) => setSelected(s => ({ ...s, [id]: !s[id] }));
  const toggleAll = () => {
    if (recs.every(r => selected[r.sku_id])) setSelected({});
    else setSelected(Object.fromEntries(recs.map(r => [r.sku_id, true])));
  };

  const totalSelectedKg = recs.filter(r => selected[r.sku_id])
    .reduce((a, r) => a + (parseFloat(overrides[r.sku_id]) || r.planned_qty_kg), 0);

  const approve = async () => {
    const items = recs.filter(r => selected[r.sku_id]).map(r => ({
      sku_id: r.sku_id,
      planned_qty_kg: parseFloat(overrides[r.sku_id]) || r.planned_qty_kg,
    }));
    if (items.length === 0) return toast.error("Select at least one recommendation");
    setLoading(true);
    try {
      const { data } = await api.post("/planner/approve", { items });
      toast.success(`${data.created} batches created`);
      setSelected({}); setOverrides({});
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approval failed");
    } finally { setLoading(false); }
  };

  const highPriority = recs.filter(r => r.priority !== "normal").length;
  const criticalCount = recs.filter(r => r.priority === "critical").length;

  return (
    <div data-testid="planning-page">
      <PageHeader subtitle="Smart Planner · Demand-driven" title="Production Recommendations for Today"
        actions={
          <Button onClick={approve} disabled={loading || Object.values(selected).filter(Boolean).length === 0} data-testid="approve-plan-btn">
            <CheckCircle size={16} className="mr-1.5"/>
            Approve & Create ({Object.values(selected).filter(Boolean).length})
          </Button>
        }
      />

      {settings && (
        <div className="mb-4 flex items-center gap-2 flex-wrap text-[11px]">
          <span className="inline-flex items-center gap-1 text-muted-foreground">
            <Info size={12}/>Using planner rules:
          </span>
          <Badge variant="secondary" className="mono text-[10px]">safety {settings.safety_days}d</Badge>
          <Badge variant="secondary" className="mono text-[10px]">target {settings.target_days}d</Badge>
          <Badge variant="secondary" className="mono text-[10px]">min floor {settings.min_stock_floor}</Badge>
          <Badge variant="secondary" className="mono text-[10px]">target floor {settings.target_stock_floor}</Badge>
          <Badge variant="secondary" className="mono text-[10px]">trend window {settings.trend_lookback_days}d vs {settings.baseline_lookback_days}d</Badge>
          <span className="text-muted-foreground">· edit in Settings</span>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Card className="shadow-none"><CardContent className="p-4"><div className="overline mb-2">Recommendations</div><div className="text-metric text-3xl">{recs.length}</div></CardContent></Card>
        <Card className="shadow-none"><CardContent className="p-4"><div className="overline mb-2">Critical / High</div><div className="text-metric text-3xl text-red-600">{criticalCount}<span className="text-base text-muted-foreground"> / {highPriority - criticalCount}</span></div></CardContent></Card>
        <Card className="shadow-none"><CardContent className="p-4"><div className="overline mb-2">Selected Load</div><div className="text-metric text-3xl text-primary">{totalSelectedKg.toFixed(1)}<span className="text-base">kg</span></div><Progress value={Math.min(100, (totalSelectedKg/totalCap)*100)} className="h-1.5 mt-2"/></CardContent></Card>
        <Card className="shadow-none"><CardContent className="p-4"><div className="overline mb-2">Machine Capacity</div><div className="text-metric text-3xl">{totalCap}<span className="text-base">kg</span></div><div className="text-[10px] text-muted-foreground mt-1">Combined dryers</div></CardContent></Card>
      </div>

      <Card className="shadow-none">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"><Checkbox onCheckedChange={toggleAll} data-testid="select-all-plan"/></TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>SKU</TableHead>
                <TableHead className="text-right">Velocity</TableHead>
                <TableHead className="text-right">Cover</TableHead>
                <TableHead className="text-right">Stock / Min / Target</TableHead>
                <TableHead className="text-right">Gap</TableHead>
                <TableHead>Why</TableHead>
                <TableHead className="text-right w-28">Plan (kg)</TableHead>
                <TableHead>Machine</TableHead>
                <TableHead>Material</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recs.map(r => (
                <TableRow key={r.sku_id} className={selected[r.sku_id] ? "bg-primary/5" : ""} data-testid={`plan-row-${r.sku_code}`}>
                  <TableCell><Checkbox checked={!!selected[r.sku_id]} onCheckedChange={()=>toggle(r.sku_id)} data-testid={`plan-select-${r.sku_code}`}/></TableCell>
                  <TableCell>
                    <Badge className={PRIORITY_BADGE[r.priority]}>{r.priority.toUpperCase()}</Badge>
                    {r.has_override && <Badge variant="outline" className="ml-1 text-[9px]">override</Badge>}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm font-medium">{r.sku_name}</div>
                    <div className="mono text-[11px] text-muted-foreground">{r.sku_code} · {r.category}</div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="mono text-xs">{r.sales_velocity_pouches_per_day}/d</div>
                    <TrendChip pct={r.trend_pct}/>
                  </TableCell>
                  <TableCell className="text-right mono text-xs">
                    {r.days_of_cover !== null ? (
                      <span className={r.days_of_cover < r.safety_days ? "text-red-600 font-semibold" : "text-muted-foreground"}>
                        {r.days_of_cover}d
                      </span>
                    ) : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-right mono text-xs">
                    <span className={r.current_stock < r.min_stock ? "text-red-600 font-semibold" : ""}>{r.current_stock}</span>
                    <span className="text-muted-foreground"> / {r.min_stock} / {r.target_stock}</span>
                  </TableCell>
                  <TableCell className="text-right mono font-semibold">{r.gap_pouches}</TableCell>
                  <TableCell className="max-w-[260px]">
                    <div className="flex flex-wrap gap-1">
                      {(r.reasons || []).map((rn, i) => (
                        <span key={i}
                              className={`inline-block text-[10px] px-1.5 py-0.5 rounded ${REASON_TONE[rn.tone] || REASON_TONE.muted}`}
                              data-testid={`plan-reason-${r.sku_code}-${rn.key}`}>
                          {rn.label}
                        </span>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Input type="number" defaultValue={r.planned_qty_kg}
                      className="h-8 text-right mono w-24 ml-auto"
                      onChange={e=>setOverrides({...overrides, [r.sku_id]: e.target.value})}
                      data-testid={`plan-qty-${r.sku_code}`}/>
                  </TableCell>
                  <TableCell className="text-xs">
                    {r.machine_plan && r.machine_plan.length > 0 ? (
                      <div className="space-y-0.5">
                        {r.machine_plan.map((mp, i) => (
                          <div key={i} className="mono text-[11px]">{mp.machine_name} <span className="text-muted-foreground">· {mp.qty_kg}kg</span></div>
                        ))}
                      </div>
                    ) : <span className="text-red-600">No fit</span>}
                  </TableCell>
                  <TableCell>
                    {r.can_produce
                      ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">OK</Badge>
                      : <Badge className="bg-red-100 text-red-700 hover:bg-red-100 cursor-help" title={r.material_shortfalls.map(s=>`${s.material}: need ${s.need}, have ${s.have}`).join('\n')}>SHORT</Badge>
                    }
                  </TableCell>
                </TableRow>
              ))}
              {recs.length === 0 && (
                <TableRow><TableCell colSpan={11} className="text-center py-8 text-muted-foreground">All finished goods above target · No production needed today</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
